import { useState } from 'react';
import { BrainIcon } from 'lucide-react';
import { enhanceSnapText } from '@/lib/api';
import Modal from '@/components/Modal';
import Button from '@/components/form/Button';

interface EnhanceTextModalProps {
  isOpen: boolean;
  onClose: () => void;
  question: string;
  description: string;
  onAccept: (enhancedQuestion: string, enhancedDescription: string) => void;
}

export default function EnhanceTextModal({
  isOpen,
  onClose,
  question,
  description,
  onAccept
}: EnhanceTextModalProps) {
  const [loading, setLoading] = useState(false);
  const [enhancedText, setEnhancedText] = useState<{
    enhancedQuestion: string;
    enhancedQuestionDetails: string;
  } | null>(null);

  const handleEnhance = async () => {
    try {
      setLoading(true);
      const response = await enhanceSnapText(question, description);
      setEnhancedText(response);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = () => {
    if (enhancedText) {
      onAccept(enhancedText.enhancedQuestion, enhancedText.enhancedQuestionDetails);
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Enhance Text with AI"
    >
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Original Text */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-2">Original Text</h3>
            <div className="space-y-3 bg-gray-50 p-4 rounded-lg h-full">
              <div>
                <div className="text-sm font-medium text-gray-600">Question:</div>
                <div className="mt-1 text-gray-900">{question}</div>
              </div>
              {description && (
                <div>
                  <div className="text-sm font-medium text-gray-600">Description:</div>
                  <div className="mt-1 text-gray-900">{description}</div>
                </div>
              )}
            </div>
          </div>

          {/* Enhanced Text */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-2">Enhanced Text</h3>
            <div className="space-y-3 bg-primary-50 p-4 rounded-lg h-full">
              {enhancedText ? (
                <>
                  <div>
                    <div className="text-sm font-medium text-gray-600">Question:</div>
                    <div className="mt-1 text-gray-900">{enhancedText.enhancedQuestion}</div>
                  </div>
                  {enhancedText.enhancedQuestionDetails && (
                    <div>
                      <div className="text-sm font-medium text-gray-600">Description:</div>
                      <div className="mt-1 text-gray-900">{enhancedText.enhancedQuestionDetails}</div>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  Click "Enhance with AI" to see the improved version
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t">
          {!enhancedText ? (
            <Button
              onClick={handleEnhance}
              loading={loading}
              icon={<BrainIcon className="h-4 w-4" />}
            >
              Enhance with AI
            </Button>
          ) : (
            <>
              <Button
                variant="ghost"
                onClick={onClose}
              >
                Keep Original
              </Button>
              <Button
                onClick={handleAccept}
                icon={<BrainIcon className="h-4 w-4" />}
              >
                Use Enhanced Text
              </Button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}